import { Link } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { AuthorCard } from "@/components/site/AuthorCard";
import { ReadingProgress } from "@/components/site/ReadingProgress";
import { ArticleTOC } from "@/components/site/ArticleTOC";
import { ShareButtons } from "@/components/site/ShareButtons";
import type { Locale } from "@/lib/i18n";
import { SITE_URL } from "@/lib/i18n";

export interface PillarSection {
  /** Stable anchor id (slug) used for the in-page TOC. */
  id: string;
  heading: string;
  /**
   * Each entry is one block.
   * - Line starting with "### " → <h3>
   * - Line starting with "- " → bullet (consecutive bullets group into one <ul>)
   * - Otherwise → <p>
   */
  blocks: string[];
}

export interface PillarFAQ {
  q: string;
  a: string;
}

export interface PillarRelated {
  label: string;
  href: string;
}

export interface PillarCTA {
  eyebrow?: string;
  title: string;
  body: string;
  href: string;
  label: string;
}

/**
 * Editorial image for image search and previews. Render a real HTML <img> when supplied.
 */
export interface PillarCover {
  src: string;
  alt: string;
  srcSet?: string;
  sizes?: string;
}

export interface PillarArticleProps {
  locale: Locale;
  path: string;
  eyebrow: string;
  title: string;
  intro: string;
  updated: string;
  readingMinutes: number;
  hubHref: string;
  hubLabel: string;
  sections: PillarSection[];
  midCTA: PillarCTA;
  finalCTA: PillarCTA;
  related?: PillarRelated[];
  faqs?: PillarFAQ[];
  publishedTime?: string;
  modifiedTime?: string;
  cover?: PillarCover;
  guideLabel?: string;
  guideLabelEs?: string;
  afterIntro?: React.ReactNode;
}

function renderBlocks(blocks: string[]) {
  const out: React.ReactNode[] = [];
  let buf: string[] = [];

  const renderInline = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    const re = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|`([^`]+)`/g;
    let last = 0;
    let match: RegExpExecArray | null;
    let key = 0;

    while ((match = re.exec(text)) !== null) {
      if (match.index > last) parts.push(text.slice(last, match.index));

      if (match[1] && match[2]) {
        const label = match[1];
        const href = match[2];
        const external = /^https?:\/\//i.test(href);
        parts.push(
          <a
            key={`l-${key++}`}
            href={href}
            className="font-medium text-foreground underline underline-offset-4 hover:opacity-80"
            {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          >
            {label}
          </a>,
        );
      } else if (match[3]) {
        parts.push(
          <strong key={`s-${key++}`} className="font-semibold text-foreground">
            {match[3]}
          </strong>,
        );
      } else if (match[4]) {
        parts.push(
          <code key={`c-${key++}`} className="rounded bg-foreground/5 px-1.5 py-0.5 font-mono text-[0.92em] text-foreground">
            {match[4]}
          </code>,
        );
      }

      last = match.index + match[0].length;
    }

    if (last < text.length) parts.push(text.slice(last));
    return parts.length === 0 ? text : parts;
  };

  const flushList = (key: string) => {
    if (!buf.length) return;
    out.push(
      <ul key={`ul-${key}`} className="my-5 list-disc space-y-2 pl-6 text-base leading-relaxed text-foreground/85">
        {buf.map((item, index) => (
          <li key={index}>{renderInline(item)}</li>
        ))}
      </ul>,
    );
    buf = [];
  };

  blocks.forEach((raw, index) => {
    const line = raw.trim();
    if (line.startsWith("- ")) {
      buf.push(line.slice(2));
      return;
    }

    flushList(String(index));

    if (line.startsWith("### ")) {
      out.push(
        <h3 key={`h3-${index}`} className="mt-10 font-display text-xl font-semibold tracking-tight text-foreground">
          {renderInline(line.slice(4))}
        </h3>,
      );
      return;
    }

    out.push(
      <p key={`p-${index}`} className="mt-5 text-base leading-relaxed text-foreground/85">
        {renderInline(line)}
      </p>,
    );
  });

  flushList("end");
  return out;
}

export function PillarArticle(props: PillarArticleProps) {
  const {
    locale,
    path,
    eyebrow,
    title,
    intro,
    updated,
    readingMinutes,
    hubHref,
    hubLabel,
    sections,
    midCTA,
    finalCTA,
    cover,
    related,
    faqs,
    publishedTime,
    modifiedTime,
    guideLabel,
    guideLabelEs,
    afterIntro,
  } = props;

  const tocItems = sections.map((section) => ({
    id: section.id,
    text: section.heading,
    level: 2 as const,
  }));
  const shareUrl = `${SITE_URL}${path}`;
  const t = locale === "es"
    ? {
        back: "← Recursos",
        read: "min de lectura",
        updated: "Actualizado",
        guide: guideLabelEs ?? "Guía pilar",
        relatedTitle: "Lecturas relacionadas en este clúster",
        faqTitle: "Preguntas frecuentes",
        audience: "Trabajamos típicamente con tiendas Shopify y Shopify Plus que facturan $500k+ al año.",
      }
    : {
        back: "← Resources",
        read: "min read",
        updated: "Updated",
        guide: guideLabel ?? "Pillar guide",
        relatedTitle: "Related reading in this cluster",
        faqTitle: "Frequently asked",
        audience: "We typically work with Shopify and Shopify Plus stores doing $500k+ in annual revenue.",
      };

  return (
    <article className="pb-32">
      <ReadingProgress />

      <header className="border-b border-border bg-card/20">
        <div className="mx-auto max-w-4xl px-6 py-16 sm:py-20">
          <Link
            to={hubHref}
            className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-foreground"
          >
            {t.back}
          </Link>
          <p className="mt-8 text-[10px] font-semibold uppercase tracking-[0.28em] text-muted-foreground">
            <span className="rounded-full bg-foreground/5 px-2.5 py-1 text-foreground/70">{t.guide}</span>
            <span aria-hidden className="mx-2">·</span>
            {eyebrow}
          </p>
          <h1 className="mt-6 max-w-4xl text-balance font-display text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            {title}
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground">{intro}</p>
          <p className="mt-7 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            {t.updated} {updated} · {readingMinutes} {t.read}
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.audience}</p>
          <AuthorCard variant="byline" locale={locale} publishedAt={publishedTime} reviewedAt={modifiedTime} />
          <ShareButtons url={shareUrl} title={title} locale={locale} className="mt-6" />
        </div>
      </header>

      {cover ? (
        <figure className="mx-auto mt-10 max-w-4xl px-6">
          <img
            src={cover.src}
            srcSet={cover.srcSet}
            sizes={cover.sizes}
            alt={cover.alt}
            width={1280}
            height={800}
            loading="lazy"
            decoding="async"
            className="block h-auto w-full rounded-2xl object-cover"
          />
        </figure>
      ) : null}

      <div
        className={`mx-auto mt-16 px-6 ${
          tocItems.length >= 3
            ? "max-w-6xl lg:grid lg:grid-cols-[minmax(0,1fr)_220px] lg:gap-12"
            : "max-w-3xl"
        }`}
      >
        <div className={tocItems.length >= 3 ? "min-w-0" : ""}>
          {afterIntro ? <Reveal>{afterIntro}</Reveal> : null}
          <div>
            {sections.map((section, index) => (
              <Reveal key={section.id}>
                <section className="mt-12 scroll-mt-28" id={section.id}>
                  <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground">
                    {section.heading}
                  </h2>
                  {renderBlocks(section.blocks)}
                </section>
                {index === Math.floor(sections.length / 2) - 1 ? <PillarInlineCTA cta={midCTA} /> : null}
              </Reveal>
            ))}
          </div>

          {faqs && faqs.length > 0 ? (
            <section className="mt-20 scroll-mt-28" id="faq">
              <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground">{t.faqTitle}</h2>
              <div className="mt-8 space-y-3">
                {faqs.map((faq) => (
                  <details
                    key={faq.q}
                    className="group rounded-2xl border border-border bg-card/40 p-5 transition-colors open:bg-card"
                  >
                    <summary className="flex cursor-pointer list-none items-start justify-between gap-6">
                      <h3 className="font-display text-base font-semibold tracking-tight text-foreground">{faq.q}</h3>
                      <span className="mt-1 text-muted-foreground transition-transform group-open:rotate-45" aria-hidden>+</span>
                    </summary>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
                  </details>
                ))}
              </div>
            </section>
          ) : null}

          {related && related.length > 0 ? (
            <section className="mt-20">
              <h2 className="font-display text-2xl font-semibold tracking-tight text-foreground">{t.relatedTitle}</h2>
              <ul className="mt-6 space-y-3">
                {related.map((item) => (
                  <li key={item.href}>
                    <a
                      href={item.href}
                      className="flex items-center justify-between rounded-2xl border border-border bg-card/30 px-5 py-4 text-sm text-foreground transition-colors hover:border-foreground/30 hover:bg-card"
                    >
                      <span className="font-medium">{item.label}</span>
                      <span aria-hidden className="text-muted-foreground">→</span>
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <a href={hubHref} className="text-sm font-medium text-foreground underline underline-offset-4 hover:opacity-80">
                  {hubLabel} →
                </a>
              </div>
            </section>
          ) : null}

          <PillarFinalCTA cta={finalCTA} />
          <ShareButtons url={shareUrl} title={title} locale={locale} className="mt-12" />
          <AuthorCard variant="full" locale={locale} publishedAt={publishedTime} reviewedAt={modifiedTime} />
        </div>

        {tocItems.length >= 3 ? <ArticleTOC items={tocItems} locale={locale} /> : null}
      </div>
    </article>
  );
}

function PillarInlineCTA({ cta }: { cta: PillarCTA }) {
  return (
    <div className="my-12 rounded-3xl border border-border bg-card p-8 sm:p-10">
      {cta.eyebrow ? (
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">{cta.eyebrow}</p>
      ) : null}
      <h3 className="mt-3 font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">{cta.title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">{cta.body}</p>
      <a
        href={cta.href}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm font-medium text-background transition-opacity hover:opacity-90"
      >
        {cta.label} →
      </a>
    </div>
  );
}

function PillarFinalCTA({ cta }: { cta: PillarCTA }) {
  return (
    <section className="mt-20 rounded-3xl border border-border bg-foreground p-10 text-background sm:p-14">
      {cta.eyebrow ? (
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-background/60">{cta.eyebrow}</p>
      ) : null}
      <h2 className="mt-3 font-display text-3xl font-semibold leading-[1.05] tracking-tight sm:text-4xl">{cta.title}</h2>
      <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background/80 sm:text-base">{cta.body}</p>
      <a
        href={cta.href}
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-background px-6 py-3 text-sm font-medium text-foreground transition-opacity hover:opacity-90"
      >
        {cta.label} →
      </a>
    </section>
  );
}

export function articleJsonLdForPillar(opts: {
  title: string;
  description: string;
  path: string;
  image?: string;
  publishedTime: string;
  modifiedTime: string;
  section: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: opts.title,
    description: opts.description,
    ...(opts.image ? { image: [new URL(opts.image, SITE_URL).href] } : {}),
    datePublished: opts.publishedTime,
    dateModified: opts.modifiedTime,
    articleSection: opts.section,
    author: {
      "@type": "Person",
      name: "Samuel Noriega",
      url: "https://www.linkedin.com/in/samuelnoriega/",
      jobTitle: "Founder, Shugert Marketing",
    },
    publisher: {
      "@type": "Organization",
      name: "Shugert",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
    },
    mainEntityOfPage: `${SITE_URL}${opts.path}`,
  };
}
